import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";

import {
    ROUTE_ACKNOWLEDGE_EARN_VAULT,
    ROUTE_GET_EARN_PORTFOLIO_EVENTS,
    ROUTE_GET_EARN_PORTFOLIO_HISTORY,
    ROUTE_GET_EARN_PORTFOLIO_POSITIONS,
    ROUTE_GET_EARN_PORTFOLIO_SUMMARY,
    ROUTE_GET_EARN_POSITION_CHART,
    ROUTE_GET_EARN_VAULT_POSITION
} from "../constants";
import {
    AcknowledgeEarnVaultResponseApi,
    EarnVaultPositionResponseApi,
    EarnWalletRequestApi,
    EarnWalletVaultRequestApi
} from "../types/api/earn-positions";
import {
    EarnPortfolioEventRowApi,
    EarnPortfolioEventsRequestApi,
    EarnPortfolioHistoryPointApi,
    EarnPortfolioHistoryRequestApi,
    EarnPositionChartRequestApi,
    EarnPositionChartResponseApi
} from "../types/api/earn-events";
import {
    EarnPortfolioEvent,
    EarnPortfolioHistoryPoint,
    EarnPortfolioSummaryPosition,
    EarnPositionChartResponse,
    EarnVaultPositionResponse
} from "../types";
import { EarnPortfolioSummaryPositionApi } from "../types/api/earn-summary";
import {
    parseEarnPortfolioPositionsFromApi,
    parseEarnVaultPositionResponseFromApi
} from "../types/parsers/earn-positions-parser";
import { parseEarnPortfolioSummaryFromApi } from "../types/parsers/earn-summary-parser";
import {
    parseEarnPortfolioEventsFromApi,
    parseEarnPortfolioHistoryFromApi,
    parseEarnPositionChartFromApi
} from "../types/parsers/earn-events-parser";
import { abfSerializeQueryArgs, marketsBaseQuery } from "./util";

const EARN_POSITION_TAG = "EarnPosition";

export const earnVaultApi = createApi({
    reducerPath: "abfEarnVaultApi",
    baseQuery: marketsBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,
    tagTypes: [EARN_POSITION_TAG],
    endpoints: (builder) => ({
        earnVaultPosition: builder.query<EarnVaultPositionResponse, EarnWalletVaultRequestApi>({
            providesTags: [EARN_POSITION_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_EARN_VAULT_POSITION,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: EarnVaultPositionResponseApi): EarnVaultPositionResponse =>
                parseEarnVaultPositionResponseFromApi(response)
        }),
        earnPortfolioPositions: builder.query<EarnVaultPositionResponse[], EarnWalletRequestApi>({
            providesTags: [EARN_POSITION_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_EARN_PORTFOLIO_POSITIONS,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: EarnVaultPositionResponseApi[]): EarnVaultPositionResponse[] =>
                parseEarnPortfolioPositionsFromApi(response)
        }),
        earnPortfolioSummary: builder.query<EarnPortfolioSummaryPosition[], EarnWalletRequestApi>({
            providesTags: [EARN_POSITION_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_EARN_PORTFOLIO_SUMMARY,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: EarnPortfolioSummaryPositionApi[]): EarnPortfolioSummaryPosition[] =>
                parseEarnPortfolioSummaryFromApi(response)
        }),
        earnPortfolioEvents: builder.query<EarnPortfolioEvent[], EarnPortfolioEventsRequestApi>({
            providesTags: [EARN_POSITION_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_EARN_PORTFOLIO_EVENTS,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: EarnPortfolioEventRowApi[]): EarnPortfolioEvent[] =>
                parseEarnPortfolioEventsFromApi(response)
        }),
        earnPortfolioHistory: builder.query<EarnPortfolioHistoryPoint[], EarnPortfolioHistoryRequestApi>({
            providesTags: [EARN_POSITION_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_EARN_PORTFOLIO_HISTORY,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: EarnPortfolioHistoryPointApi[]): EarnPortfolioHistoryPoint[] =>
                parseEarnPortfolioHistoryFromApi(response)
        }),
        earnPositionChart: builder.query<EarnPositionChartResponse, EarnPositionChartRequestApi>({
            providesTags: [EARN_POSITION_TAG],
            query(body) {
                return {
                    url: ROUTE_GET_EARN_POSITION_CHART,
                    method: "POST",
                    body
                };
            },
            transformResponse: (response: EarnPositionChartResponseApi): EarnPositionChartResponse =>
                parseEarnPositionChartFromApi(response)
        }),
        acknowledgeEarnVault: builder.mutation<AcknowledgeEarnVaultResponseApi, EarnWalletVaultRequestApi>({
            // Invalidate the per-vault position (and the wallet's portfolio list) so
            // `acknowledgedAt` refreshes from the BE after a successful ack.
            invalidatesTags: [EARN_POSITION_TAG],
            query(body) {
                return {
                    url: ROUTE_ACKNOWLEDGE_EARN_VAULT,
                    method: "POST",
                    body
                };
            }
        })
    })
});

export const {
    useEarnVaultPositionQuery,
    useEarnPortfolioPositionsQuery,
    useEarnPortfolioSummaryQuery,
    useEarnPortfolioEventsQuery,
    useEarnPortfolioHistoryQuery,
    useEarnPositionChartQuery,
    useAcknowledgeEarnVaultMutation
} = earnVaultApi;

export function useEarnVaultApi() {
    const dispatch = useDispatch();

    return {
        resetEarnVaultPositionsApi: () => dispatch(earnVaultApi.util.invalidateTags([EARN_POSITION_TAG]))
    };
}
